import { NotificationDTO } from '@iota/iota-management-portal-v4-client';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, createSelector, on } from '@ngrx/store';
import moment from 'moment';
import {
    LoadNotificationsFailure,
    LoadNotificationsSuccess,
    ResetSendMail,
    SendMail,
    SendMailFailure,
    SendMailSuccess,
    SendNotification,
    SendNotificationFailure,
    SendNotificationSuccess
} from '../actions/notification.actions';
import { AppState } from '../store';

export interface NotificationState extends EntityState<NotificationDTO> {
    sending: boolean;
    sendSuccess: boolean;
    sendFailure: boolean;
    error: any;
}

function selectNotificationId(a: NotificationDTO): string {
    return a.notificationId;
}

function sortNotifications(a: NotificationDTO, b: NotificationDTO): number {
    return moment(b.creationDateTime).toDate().getTime() - moment(a.creationDateTime).toDate().getTime();
}

export const notificationAdapter: EntityAdapter<NotificationDTO> = createEntityAdapter<NotificationDTO>({
    selectId: selectNotificationId,
    sortComparer: sortNotifications
});

export const initialState: NotificationState = notificationAdapter.getInitialState({
    sending: false,
    sendSuccess: false,
    sendFailure: false,
    error: undefined
});

export const notificationReducer = createReducer(
    initialState,
    on(LoadNotificationsSuccess, (state, { notifications }) =>
        notificationAdapter.upsertMany(Array.isArray(notifications) ? notifications : [], state)
    ),
    on(LoadNotificationsFailure, (state, { error }) => ({ ...state, error })),

    on(SendNotification, (state) => ({ ...state, sending: true })),
    on(SendNotificationSuccess, (state, { notification }) =>
        notificationAdapter.upsertOne(notification, {
            ...state,
            sending: false
        })
    ),
    on(SendNotificationFailure, (state, { error }) => ({ ...state, error, sending: false })),

    on(SendMail, (state) => ({ ...state, sending: true, sendSuccess: false, sendFailure: false })),
    on(SendMailSuccess, (state) => ({ ...state, sending: false, sendSuccess: true, sendFailure: false })),
    on(SendMailFailure, (state, { error }) => ({ ...state, error, sendSuccess: false, sendFailure: true })),
    on(ResetSendMail, (state) => ({ ...state, sending: false, sendSuccess: false, sendFailure: false }))
);

const { selectAll } = notificationAdapter.getSelectors();

export const notificationState = (state: AppState) => state.notificationState;
export const allNotifications = createSelector(notificationState, selectAll);
export const notificationSending = createSelector(notificationState, (state) => state.sending);
export const sending = createSelector(notificationState, (state) => state.sending);
export const sendSuccess = createSelector(notificationState, (state) => state.sendSuccess);
export const sendFailure = createSelector(notificationState, (state) => state.sendFailure);

export const notificationsByReceiverName = (userName: string) =>
    createSelector(allNotifications, (allNotifications) => allNotifications.filter((n) => n.receiver === userName));

export const notificationsByReceiverId = (userId: string) =>
    createSelector(allNotifications, (allNotifications) => allNotifications.filter((n) => n.receiverId === userId));
